export const ORDER_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isOrderUuid = (value) => ORDER_UUID_RE.test(String(value ?? '').trim());

export const orderIdsMatch = (first, second) =>
  isOrderUuid(first) &&
  isOrderUuid(second) &&
  String(first).trim().toLowerCase() === String(second).trim().toLowerCase();

// Only these ledger event types use order references. Other event types can
// also contain colon-delimited identifiers, such as transfer legs or storage
// snapshots, but those must not become misleading order links.
const ORDER_REFERENCE_EVENT_TYPES = new Set([
  'Pick',
  'Pack',
  'Ship',
  'Box',
  'Receive',
  'Return',
  'ReturnInspection',
  'Packaging',
  'Insurance',
  'ShippingAdjustment',
]);

/**
 * Split a documented order reference (`orderId` or `orderId:suffix`) without
 * losing the suffix. The suffix may itself contain colons, so only the first
 * separator is significant.
 */
export const getOrderReferenceParts = (referenceId, eventType) => {
  if (!ORDER_REFERENCE_EVENT_TYPES.has(eventType)) return null;

  const reference = String(referenceId ?? '').trim();
  const separatorIndex = reference.indexOf(':');
  if (separatorIndex === -1) return reference ? { orderId: reference, suffix: '' } : null;
  if (separatorIndex <= 0 || separatorIndex === reference.length - 1) return null;

  const orderId = reference.slice(0, separatorIndex).trim();
  const suffix = reference.slice(separatorIndex + 1).trim();
  if (!orderId || !suffix) return null;

  return { orderId, suffix };
};

const nonUuidValue = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text && !isOrderUuid(text) ? text : '';
};

const normalizedIdentifier = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isExactHumanIdentifierValue = (value, normalizedQuery) => {
  const humanIdentifier = nonUuidValue(value);
  if (!humanIdentifier) return false;

  const normalizedValue = normalizedIdentifier(humanIdentifier);
  if (normalizedValue === normalizedQuery) return true;

  // Numeric order numbers are often displayed with a source prefix
  // (e.g. SJ-7224). Only allow this narrow human-readable form so a
  // number found inside an unrelated UUID cannot become an exact match.
  if (!/^\d+$/.test(normalizedQuery)) return false;
  const prefixedMatch = humanIdentifier.match(/^[a-z][a-z0-9]*-(\d+)$/i);
  return prefixedMatch?.[1] === normalizedQuery;
};

export const getOrderIdentifierLabel = (result = {}) =>
  nonUuidValue(result.displayId) ||
  nonUuidValue(result.externalId) ||
  nonUuidValue(result.friendlyId) ||
  nonUuidValue(result.name) ||
  nonUuidValue(result.orderId) ||
  (result.orderId ? String(result.orderId) : 'Unknown order');

export const getOrderSearchResults = (response) => {
  if (Array.isArray(response?.orders)) return response.orders;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data?.orders)) return response.data.orders;
  return [];
};

export const rankOrderSearchResults = (results, query) => {
  if (!Array.isArray(results)) return results;

  const normalizedQuery = normalizedIdentifier(query);
  if (!normalizedQuery) return results;

  return results
    .map((result, index) => ({
      result,
      index,
      exactHumanMatch: [result?.displayId, result?.externalId, result?.friendlyId].some((value) =>
        isExactHumanIdentifierValue(value, normalizedQuery),
      ),
    }))
    .sort((first, second) => {
      if (first.exactHumanMatch !== second.exactHumanMatch) {
        return first.exactHumanMatch ? -1 : 1;
      }
      return first.index - second.index;
    })
    .map(({ result }) => result);
};

const isExactIdentifierMatch = (result, query) => {
  const normalizedQuery = normalizedIdentifier(query);
  if (!normalizedQuery) return false;
  return [result?.displayId, result?.externalId, result?.friendlyId, result?.name, result?.orderId]
    .filter((value) => value != null && value !== '')
    .some((value) => {
      if (normalizedIdentifier(value) === normalizedQuery) return true;
      return isExactHumanIdentifierValue(value, normalizedQuery);
    });
};

/**
 * Resolve a user-entered order identifier through the typed search endpoint.
 * The returned rawOrderId is the only value that should be sent to UUID-only
 * order detail, timeline, and event endpoints.
 */
export async function resolveOrderIdentifier(query, { searchFn, storeId } = {}) {
  const normalizedQuery = String(query ?? '').trim();
  if (normalizedQuery.length < 2) {
    return {
      status: 'too_short',
      message: 'Enter at least 2 characters to find an order.',
    };
  }
  if (typeof searchFn !== 'function') {
    return { status: 'error', message: 'Order search is unavailable. Please try again.' };
  }

  let response;
  try {
    response = await searchFn(normalizedQuery, storeId);
  } catch {
    return { status: 'error', message: 'Order search failed. Please try again.' };
  }
  if (response?.success === false) {
    return {
      status: 'error',
      message: response.message || 'Order search failed. Please try again.',
    };
  }

  const searchResults = getOrderSearchResults(response);
  const results = searchResults
    .map((result) => {
      const rawOrderId = result?.orderId;
      return !isOrderUuid(rawOrderId)
        ? null
        : {
            ...result,
            rawOrderId: String(rawOrderId),
            label: getOrderIdentifierLabel(result),
          };
    })
    .filter(Boolean);
  const uniqueResults = results.filter(
    (result, index, all) =>
      all.findIndex((candidate) => candidate.rawOrderId === result.rawOrderId) === index,
  );
  const exactResults = uniqueResults.filter((result) =>
    isExactIdentifierMatch(result, normalizedQuery),
  );
  const relevantResults = exactResults.length > 0 ? exactResults : uniqueResults;

  if (relevantResults.length === 0) {
    if (searchResults.length > 0) {
      return {
        status: 'error',
        message: 'Order search returned an invalid identifier. Please try again.',
      };
    }
    return {
      status: 'no_match',
      message: 'No matching order. Try an external ID, SKUSavvy ID, or UUID.',
    };
  }
  if (relevantResults.length > 1) {
    return {
      status: 'ambiguous',
      message: 'More than one order matches. Select the exact order below.',
      results: relevantResults,
    };
  }
  return { status: 'found', result: relevantResults[0], results: relevantResults };
}
